import React from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { BaseButton } from './BaseButton';
import { BaseTabs } from './BaseTab';
import { getControlComponent } from './controls/ControlRegistry';
import { initializeControls } from './controls/initializeControls';

// Initialize controls ONCE at module load - not every render
initializeControls();

/**
 * Normalize property names: replace spaces with underscores, preserve casing.
 * e.g., "Header Navigate" → "Header_Navigate", "startDate" → "startDate"
 */
export const normalizePropertyName = (name: string): string => {
  return name.replace(/\s+/g, '_');
};

export interface SchemaFormFieldProps {
  schema: Record<string, any>;
  value: any;
  onChange: (value: any) => void;
  fieldPath: string;
}

/**
 * Renders a single form field based on x-control definition
 */
export const SchemaFormField: React.FC<SchemaFormFieldProps> = ({
  schema,
  value,
  onChange,
  fieldPath,
}) => {

  const propName = fieldPath.includes('.') 
    ? fieldPath.split('.').pop() 
    : fieldPath.includes('[')
    ? fieldPath.split('[')[0].split('.').pop()
    : fieldPath;
  const label = schema['x-control']?.label || schema.title || propName;
  const description = schema.description;
  const required = schema.required || false;
  // Render based on x-control definition
  if (schema['x-control']) {
    const controlDef = schema['x-control'];
    const controlType = controlDef.type;

    // Get control component from registry
    const ControlComponent = getControlComponent(controlType);
    if (ControlComponent) {
      // For block and array controls, pass renderChild function
      const renderChild = (controlType === 'block' || controlType === 'array' || controlType === 'collapse') 
        ? (childProps: SchemaFormFieldProps) => (
            <SchemaFormField
              schema={childProps.schema}
              value={childProps.value}
              onChange={childProps.onChange}
              fieldPath={childProps.fieldPath}
            />
          )
        : undefined;

      return (
        <ControlComponent
          name={propName || 'field'}
          path={fieldPath}
          value={value}
          onChange={onChange}
          schema={schema}
          control={controlDef}
          label={label}
          description={description}
          required={required}
          renderChild={renderChild}
        />
      );
    }
  }

  // Fallback: x-control not defined
  return (
    <Box sx={{ p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1, mb: 2 }}>
      <Typography color="warning" variant="body2">
        ⚠️ No x-control definition for "{label}". Please update schema to include x-control.
      </Typography>
    </Box>
  );
};

/**
 * Recursive wrapper for rendering nested fields
 */
export const DynamicFormField: React.FC<SchemaFormFieldProps> = (props) => {
  return <SchemaFormField {...props} />;
};

/**
 * Complete form builder component with tab support for root properties
 */
export interface DynamicFormBuilderProps {
  schema: Record<string, any>;
  initialData?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  loading?: boolean;
  hideSubmitButton?: boolean;
  formRef?: React.RefObject<HTMLFormElement | null>;
}

export const DynamicFormBuilder: React.FC<DynamicFormBuilderProps> = ({
  schema,
  initialData = {},
  onSubmit,
  loading = false,
  hideSubmitButton = false,
  formRef,
}) => {
  const [formData, setFormData] = React.useState<Record<string, any>>(initialData);
  
  // Support both formats:
  // 1. { properties: { "field-name": {...} } }  (wrapped)
  // 2. { "field-name": {...} }                   (direct)
  const schemaProperties = schema.properties || schema;
  const metadataKeys = new Set(['type', 'title', 'description', 'required', 'default', 'properties', '$schema', 'x-control', 'additionalProperties']);
  
  // MEMOIZED: Calculate root properties once per schema change to avoid infinite loop
  // Also create a mapping from original keys to normalized keys
  const { rootProperties, keyMap } = React.useMemo(() => {
    const originalKeys = Object.keys(schemaProperties).filter(
      key => !metadataKeys.has(key) && schemaProperties[key] && typeof schemaProperties[key] === 'object'
    );
    
    const mapping: Record<string, string> = {}; // originalKey -> normalizedKey
    const normalized = originalKeys.map(key => {
      const normalizedKey = normalizePropertyName(key);
      mapping[key] = normalizedKey;
      return normalizedKey;
    });
    
    return { rootProperties: normalized, keyMap: mapping };
  }, [schemaProperties]);
  
  // Get original keys for schema lookup
  const originalRootKeys = React.useMemo(() => {
    return Object.keys(schemaProperties).filter(
      key => !metadataKeys.has(key) && schemaProperties[key] && typeof schemaProperties[key] === 'object'
    );
  }, [schemaProperties]);
  
  const hasMultipleRootProps = rootProperties.length > 1;
  const [tabValue, setTabValue] = React.useState<string | number>(0);

  // Initialize tabValue and form data from schema
  React.useEffect(() => {
    // Set initial tab value
    if (hasMultipleRootProps && rootProperties.length > 0) {
      setTabValue(rootProperties[0]);
    } else {
      setTabValue(0);
    }
    
    // Initialize form data from schema default values with normalized keys
    if (Object.keys(initialData).length === 0) {
      const defaults: Record<string, any> = {};
      originalRootKeys.forEach((originalKey, index) => {
        const normalizedKey = rootProperties[index];
        const prop = schemaProperties[originalKey];
        defaults[normalizedKey] = prop.default || (prop.type === 'array' ? [] : prop.type === 'boolean' ? false : '');
      });
      setFormData(defaults);
    } else {
      // Map initialData keys to schema-normalized keys.
      // Support case-insensitive fallback to recover data saved by older versions
      // that lowercased camelCase field names (e.g. startDate → startdate).
      const lowerToInitial: Record<string, string> = {};
      Object.keys(initialData).forEach(k => { lowerToInitial[k.toLowerCase()] = k; });

      const normalizedData: Record<string, any> = {};
      originalRootKeys.forEach((originalKey, index) => {
        const normalizedKey = rootProperties[index];
        if (initialData[originalKey] !== undefined) {
          // Exact match on original key (e.g. "Video Section")
          normalizedData[normalizedKey] = initialData[originalKey];
        } else if (initialData[normalizedKey] !== undefined) {
          // Data was saved with normalized keys (e.g. "Video_Section")
          normalizedData[normalizedKey] = initialData[normalizedKey];
        } else {
          // Fallback: case-insensitive lookup on both original and normalized key
          const fallbackKey = lowerToInitial[originalKey.toLowerCase()]
            ?? lowerToInitial[normalizedKey.toLowerCase()];
          if (fallbackKey !== undefined) {
            normalizedData[normalizedKey] = initialData[fallbackKey];
          }
        }
      });
      setFormData(normalizedData);
    }
  }, [schema, initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      {/* All fields rendered as flat form (no tabs) */}
      {rootProperties.map((normalizedKey, index) => {
        const originalKey = originalRootKeys[index];
        return (
          <DynamicFormField
            key={normalizedKey}
            schema={schemaProperties[originalKey]}
            value={formData[normalizedKey]}
            onChange={(newValue: any) => {
              setFormData((prev) => ({
                ...prev,
                [normalizedKey]: newValue,
              }));
            }}
            fieldPath={normalizedKey}
          />
        );
      })}

      {!hideSubmitButton && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <BaseButton 
            type="submit" 
            variant="contained" 
            colorVariant="primary"
            disabled={loading}
            isLoading={loading}
            loadingLabel="Saving..."
          >
            Save Configuration
          </BaseButton>
        </Box>
      )}
    </form>
  );
};
